// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import { X, CheckCircle, AlertCircle, AlertTriangle, Info } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useToastStore } from '@/store'
import type { ToastItem } from '@/types'

const toastStyles: Record<ToastItem['type'], string> = {
  success: 'bg-status-successBg border-status-successBorder text-status-successText',
  error:   'bg-status-alertBg border-status-alertBorder text-status-alertText',
  warning: 'bg-status-warnBg border-status-warnBorder text-status-warnText',
  info:    'bg-status-infoBg border-sage-200 text-status-infoText',
}

const toastIcons: Record<ToastItem['type'], React.ReactNode> = {
  success: <CheckCircle  className="h-4 w-4 flex-shrink-0" />,
  error:   <AlertCircle  className="h-4 w-4 flex-shrink-0" />,
  warning: <AlertTriangle className="h-4 w-4 flex-shrink-0" />,
  info:    <Info         className="h-4 w-4 flex-shrink-0" />,
}

function Toast({ toast }: { toast: ToastItem }) {
  const { removeToast } = useToastStore()

  return (
    <div
      role="alert"
      className={cn(
        'flex items-start gap-3 rounded-xl border px-4 py-3',
        'shadow-card text-sm font-medium',
        'animate-slide-down',
        toastStyles[toast.type],
      )}
    >
      {toastIcons[toast.type]}
      <span className="flex-1 leading-snug">{toast.message}</span>
      <button
        onClick={() => removeToast(toast.id)}
        className="flex-shrink-0 opacity-60 hover:opacity-100 transition-opacity"
        aria-label="Sluiten"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}

export function ToastContainer() {
  const { toasts } = useToastStore()

  if (toasts.length === 0) return null

  return (
    <div
      className="fixed top-4 right-4 z-50 flex flex-col gap-2 w-full max-w-sm"
      aria-live="polite"
      aria-atomic="true"
    >
      {toasts.map((toast) => (
        <Toast key={toast.id} toast={toast} />
      ))}
    </div>
  )
}
